import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, UserRound, Users, UserStar } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { StatCard } from '@/components/shared/stat-card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatNumber } from '@/utils/format';

interface PageMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface Department {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  unitCount: number;
}

interface DepartmentUnitRow {
  id: string;
  name: string;
  departmentId: string;
  departmentName: string;
  memberCount: number;
  officeCount: number;
}

interface ChurchDepartmentInsights {
  departmentCount: number;
  peopleAssigned: number;
  maleCount: number;
  femaleCount: number;
  withLeadersCount: number;
  largest: Array<{ key: string; label: string; value: number }>;
}

export const DepartmentsPage = () => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [unitPage, setUnitPage] = useState(1);
  const [unitPageSize, setUnitPageSize] = useState(25);

  const list = useQuery({
    queryKey: ['people', 'departments', page, pageSize],
    queryFn: () =>
      api.get<{ data: Department[]; page: PageMeta }>(
        `/people/departments?page=${page}&pageSize=${pageSize}`,
      ),
    placeholderData: keepPreviousData,
  });

  const units = useQuery({
    queryKey: ['people', 'department-units', 'all', unitPage, unitPageSize],
    queryFn: () =>
      api.get<{ data: DepartmentUnitRow[]; page: PageMeta }>(
        `/people/department-units?page=${unitPage}&pageSize=${unitPageSize}`,
      ),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: ['people', 'departments', 'insights'],
    queryFn: () => api.get<ChurchDepartmentInsights>('/people/departments/insights'),
  });

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      api.post<Department>('/people/departments', {
        name,
        description: description || undefined,
      }),
    onSuccess: (created) => {
      toast.success(t('departments.saved'));
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['people', 'departments'] });
      void navigate(`/people/departments/${created.id}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Department>[] = [
    {
      id: 'name',
      header: t('departments.columns.name'),
      cell: (row) => (
        <span className="font-medium text-brand hover:underline">{row.name}</span>
      ),
    },
    {
      id: 'people',
      header: t('departments.columns.people'),
      cell: (row) => t('departments.peopleCount', { count: row.memberCount }),
    },
    {
      id: 'units',
      header: t('departments.columns.units'),
      cell: (row) => t('departments.unitCount', { count: row.unitCount }),
    },
    {
      id: 'description',
      header: t('departments.columns.description'),
      cell: (row) => row.description ?? '—',
      hideBelowMd: true,
    },
  ];

  const unitColumns: DataTableColumn<DepartmentUnitRow>[] = [
    {
      id: 'name',
      header: t('departments.units.columns.name'),
      cell: (row) => (
        <span className="font-medium text-brand hover:underline">{row.name}</span>
      ),
    },
    {
      id: 'department',
      header: t('departments.units.columns.department'),
      cell: (row) => row.departmentName,
    },
    {
      id: 'members',
      header: t('departments.units.columns.members'),
      cell: (row) => t('departments.units.memberCount', { count: row.memberCount }),
    },
    {
      id: 'leaders',
      header: t('departments.units.columns.leaders'),
      cell: (row) => t('departments.units.leaderCount', { count: row.officeCount }),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const unitRows = units.data?.data ?? [];
  const unitPageMeta = units.data?.page;
  const paginationLabels = {
    showing: ({ from, to, total }: { from: number; to: number; total: number }) =>
      tCommon('pagination.showing', { from, to, total }),
    perPage: tCommon('pagination.perPage'),
    first: tCommon('pagination.first'),
    previous: tCommon('pagination.previous'),
    next: tCommon('pagination.next'),
    last: tCommon('pagination.last'),
  };

  return (
    <div>
      <PageHeader
        title={t('departments.title')}
        description={t('departments.description')}
        tipId="members.departments.purpose"
        headerActions={
          <Button onClick={() => setOpen(true)}>{t('departments.create')}</Button>
        }
      />

      <CollapsibleInsights
        summary={[
          {
            label: t('departments.insights.departments'),
            value: insights.data ? formatNumber(insights.data.departmentCount) : '…',
          },
          {
            label: t('departments.insights.peopleAssigned'),
            value: insights.data ? formatNumber(insights.data.peopleAssigned) : '…',
          },
          {
            label: t('departments.insights.male'),
            value: insights.data ? formatNumber(insights.data.maleCount) : '…',
          },
          {
            label: t('departments.insights.female'),
            value: insights.data ? formatNumber(insights.data.femaleCount) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard
            label={t('departments.insights.departments')}
            value={insights.data ? formatNumber(insights.data.departmentCount) : undefined}
            icon={<Layers className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.peopleAssigned')}
            value={insights.data ? formatNumber(insights.data.peopleAssigned) : undefined}
            icon={<Users className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.male')}
            value={insights.data ? formatNumber(insights.data.maleCount) : undefined}
            icon={<UserRound className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.female')}
            value={insights.data ? formatNumber(insights.data.femaleCount) : undefined}
            icon={<UserRound className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.withLeaders')}
            value={insights.data ? formatNumber(insights.data.withLeadersCount) : undefined}
            icon={<UserStar className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>
      </CollapsibleInsights>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('departments.empty')}</p>}
        onRowClick={(row) => navigate(`/people/departments/${row.id}`)}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          className="mt-4"
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={paginationLabels}
        />
      ) : null}

      <section className="mt-10">
        <h2 className="mb-4 text-heading-sm text-fg">{t('departments.units.allTitle')}</h2>
        <DataTable
          rows={unitRows}
          columns={unitColumns}
          rowKey={(row) => row.id}
          loading={units.isLoading}
          empty={
            <p className="text-body-sm text-fg-secondary">{t('departments.units.allEmpty')}</p>
          }
          onRowClick={(row) => navigate(`/people/departments/${row.departmentId}`)}
        />
        {unitPageMeta && unitPageMeta.totalPages > 1 ? (
          <Pagination
            className="mt-4"
            page={unitPageMeta.page}
            pageSize={unitPageMeta.pageSize}
            totalItems={unitPageMeta.totalItems}
            totalPages={unitPageMeta.totalPages}
            onPageChange={setUnitPage}
            onPageSizeChange={(next) => {
              setUnitPageSize(next);
              setUnitPage(1);
            }}
            labels={paginationLabels}
          />
        ) : null}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('departments.create')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('departments.name')}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('departments.descriptionField')}</Label>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending}>
                {t('departments.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
